import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["children", "onClick", "onMouseEnter", "color", "href", "margin", "renderIcon", "iconPlacement", "isWithinText"];
var _dec, _dec2, _class, _class2;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/** @jsx jsx */
import React, { Component } from 'react';
import { View } from '@instructure/ui-view';
import { hasVisibleChildren } from '@instructure/ui-a11y-utils';
import { isActiveElement, findFocusable } from '@instructure/ui-dom-utils';
import { getElementType, getInteraction, matchComponentTypes, passthroughProps, callRenderProp } from '@instructure/ui-react-utils';
import { logWarn as warn } from '@instructure/console';
import { testable } from '@instructure/ui-testable';
import { withStyle, jsx } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
/**
---
category: components
---
**/
let Link = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = testable(), _dec(_class = _dec2(_class = (_class2 = class Link extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      hasFocus: false
    };
    this.ref = null;
    this.makeStyleProps = () => {
      return {
        containsTruncateText: this.containsTruncateText,
        hasVisibleChildren: this.hasVisibleChildren
      };
    };
    this.handleElementRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.handleClick = event => {
      const onClick = this.props.onClick;
      const interaction = this.interaction;
      if (interaction === 'disabled') {
        event.preventDefault();
        event.stopPropagation();
      } else if (typeof onClick === 'function') {
        onClick(event);
      }
    };
    this.handleFocus = event => {
      this.setState({
        hasFocus: true
      });
      if (typeof this.props.onFocus === 'function') {
        this.props.onFocus(event);
      }
    };
    this.handleBlur = event => {
      this.setState({
        hasFocus: false
      });
      if (typeof this.props.onBlur === 'function') {
        this.props.onBlur(event);
      }
    };
  }
  get _link() {
    return this.ref;
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.makeStyleProps());
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, this.makeStyleProps());
  }
  get containsTruncateText() {
    let truncateText = false;
    React.Children.forEach(this.props.children, child => {
      if (child && matchComponentTypes(child, ['TruncateText'])) {
        truncateText = true;
      }
    });
    warn(
    // if display prop is used, warn about icon or TruncateText
    !truncateText || this.props.display === void 0, '[Link] Using the display property with TruncateText may cause layout issues.');
    return truncateText;
  }
  get display() {
    if (this.props.display) {
      return this.props.display; // user-entered display property
    }

    const containsTruncateText = this.containsTruncateText;
    if (this.props.renderIcon) {
      return containsTruncateText ? 'inline-flex' : 'inline-block';
    } else {
      return containsTruncateText ? 'block' : 'auto';
    }
  }
  get interaction() {
    return getInteraction({
      props: this.props,
      interactionTypes: ['disabled']
    });
  }
  get element() {
    return getElementType(Link, this.props);
  }
  get focused() {
    return isActiveElement(this.ref);
  }
  get focusable() {
    return findFocusable(this.ref);
  }
  get hasVisibleChildren() {
    return hasVisibleChildren(this.props.children);
  }
  get role() {
    const _this$props3 = this.props,
      role = _this$props3.role,
      forceButtonRole = _this$props3.forceButtonRole,
      onClick = _this$props3.onClick;
    if (forceButtonRole) {
      return onClick && this.element !== 'button' ? 'button' : role;
    }
    return role;
  }
  focus() {
    this.ref && this.ref.focus();
  }
  renderIcon() {
    var _this$props$styles;
    warn(
    // if display prop is used, warn about icon or TruncateText
    this.props.display === void 0, '[Link] Using the display property with an icon may cause layout issues.');
    return jsx("span", {
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.icon
    }, callRenderProp(this.props.renderIcon));
  }
  render() {
    var _this$props$styles2;
    const _this$props4 = this.props,
      children = _this$props4.children,
      onClick = _this$props4.onClick,
      onMouseEnter = _this$props4.onMouseEnter,
      color = _this$props4.color,
      href = _this$props4.href,
      margin = _this$props4.margin,
      renderIcon = _this$props4.renderIcon,
      iconPlacement = _this$props4.iconPlacement,
      isWithinText = _this$props4.isWithinText,
      props = _objectWithoutProperties(_this$props4, _excluded);
    const interaction = this.interaction;
    const isDisabled = interaction === 'disabled';
    const type = this.element === 'button' || this.element === 'input' ? 'button' : void 0;
    const tabIndex = this.role === 'button' && !isDisabled ? 0 : void 0;
    return jsx(View, Object.assign({}, passthroughProps(props), {
      elementRef: this.handleElementRef,
      as: this.element,
      display: this.display,
      margin: margin,
      href: href,
      onMouseEnter: onMouseEnter,
      onClick: this.handleClick,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      "aria-disabled": isDisabled ? 'true' : void 0,
      role: this.role,
      type: type,
      tabIndex: tabIndex,
      css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.link
    }), renderIcon && iconPlacement === 'start' && this.renderIcon(), children, renderIcon && iconPlacement === 'end' && this.renderIcon());
  }
}, _class2.displayName = "Link", _class2.componentId = 'Link', _class2.propTypes = propTypes, _class2.allowedProps = allowedProps, _class2.defaultProps = {
  // Leave interaction default undefined so that `disabled` can also be supplied
  interaction: void 0,
  color: 'link',
  iconPlacement: 'start',
  isWithinText: true,
  forceButtonRole: true
}, _class2)) || _class) || _class);
export default Link;
export { Link };